import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ShoppingCart, Plus, Check, Trash2, Calendar, ChevronRight, Filter } from 'lucide-react';
import { Link } from 'react-router';
import toast from 'react-hot-toast';
import { FeatureGate } from '@/components/features/FeatureGate';
import { ShoppingListsLockedPage } from '@/components/features/FeatureLockedPage';
import { Feature } from '@/types/auth';
import { formatDateRange, toDateOnly } from '@/lib/shoppingItems';
import type { ShoppingListItem } from '@/services/shoppingLists';

/**
 * The overview of the client's lists. Each card opens the list itself at
 * /shopping-lists/:id, where items are ticked, added and edited (#330). The
 * cards only count items, so a free-text row (no ingredient, maybe no amount)
 * cannot break this page.
 */

interface ShoppingListOverview {
    id: string;
    name: string;
    isCompleted: boolean;
    createdAt: string;
    completedAt: string | null;
    startDate?: string | null;
    endDate?: string | null;
    items: Pick<ShoppingListItem, 'id' | 'isPurchased'>[];
}

type ListFilter = 'all' | 'active' | 'completed';

const FILTERS: { value: ListFilter; label: string }[] = [
    { value: 'all', label: 'All Lists' },
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Completed' },
];

function listDates(list: ShoppingListOverview): string {
    const start = toDateOnly(list.startDate);
    const end = toDateOnly(list.endDate) ?? start;
    if (start && end) return formatDateRange(start, end);
    return `Made ${new Date(list.createdAt).toLocaleDateString('en-ZA', {
        day: 'numeric',
        month: 'short',
    })}`;
}

const ShoppingListsContent = () => {
    const [selectedFilter, setSelectedFilter] = useState<ListFilter>('active');
    const queryClient = useQueryClient();

    const { data: lists = [], isLoading } = useQuery<ShoppingListOverview[]>({
        queryKey: ['shopping-lists', selectedFilter],
        queryFn: async () => {
            const params = new URLSearchParams();
            // Backend expects 'completed' param, not 'isCompleted'
            if (selectedFilter !== 'all') {
                params.append('completed', selectedFilter === 'completed' ? 'true' : 'false');
            }
            const response = await api.get(`/shopping-lists?${params.toString()}`);
            return response.data?.shoppingLists || [];
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (listId: string) => {
            await api.delete(`/shopping-lists/${listId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
            toast.success('Shopping list deleted');
        },
        onError: () => {
            toast.error('Failed to delete shopping list');
        },
    });

    const completeListMutation = useMutation({
        mutationFn: async (listId: string) => {
            await api.post(`/shopping-lists/${listId}/complete`);
        },
        onSuccess: (_data, listId) => {
            queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
            queryClient.invalidateQueries({ queryKey: ['shopping-list', listId] });
            toast.success('Shopping list completed!');
        },
        onError: () => {
            toast.error('Failed to complete shopping list');
        },
    });

    const busy = deleteMutation.isPending || completeListMutation.isPending;

    const handleDeleteList = (listId: string) => {
        if (window.confirm('Are you sure you want to delete this shopping list?')) {
            deleteMutation.mutate(listId);
        }
    };

    if (isLoading) {
        return (
            <div className="max-w-5xl mx-auto px-4 py-8 lg:px-8">
                <p className="text-muted-foreground">Loading shopping lists...</p>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto px-4 py-6 lg:px-8 lg:py-8">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <h1 className="flex items-center text-2xl sm:text-3xl font-bold text-foreground">
                    <ShoppingCart className="mr-3 size-8 text-brand-text-blue" aria-hidden="true" />
                    Shopping Lists
                </h1>
                <Button asChild>
                    <Link to="/shopping-lists/new">
                        <Plus className="size-5" />
                        New List
                    </Link>
                </Button>
            </div>

            <div className="mb-5 flex items-center gap-2">
                <Filter className="size-5 text-muted-foreground" aria-hidden="true" />
                {FILTERS.map((filter) => (
                    <button
                        key={filter.value}
                        type="button"
                        aria-pressed={selectedFilter === filter.value}
                        onClick={() => setSelectedFilter(filter.value)}
                        className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                            selectedFilter === filter.value
                                ? 'bg-brand-header text-brand-header-ink'
                                : 'bg-brand-surface text-muted-foreground hover:bg-brand-header-wash'
                        }`}
                    >
                        {filter.label}
                    </button>
                ))}
            </div>

            {lists.length === 0 ? (
                <Card className="p-10 text-center">
                    <ShoppingCart className="mx-auto mb-4 size-14 text-muted-foreground" />
                    <h2 className="mb-2 text-xl font-semibold text-foreground">
                        {selectedFilter === 'all'
                            ? 'No Shopping Lists Yet'
                            : selectedFilter === 'active'
                              ? 'No Active Lists'
                              : 'No Completed Lists'}
                    </h2>
                    <p className="mb-4 text-muted-foreground">
                        {selectedFilter === 'all'
                            ? 'Create your first shopping list or generate one from a meal plan'
                            : selectedFilter === 'active'
                              ? 'All your shopping lists have been completed!'
                              : 'Complete a shopping list to see it here'}
                    </p>
                    {selectedFilter !== 'completed' && (
                        <div className="flex flex-wrap items-center justify-center gap-3">
                            <Button asChild>
                                <Link to="/shopping-lists/new">
                                    <Plus className="size-5" />
                                    Create New List
                                </Link>
                            </Button>
                            <Button variant="outline" asChild>
                                <Link to="/my-menu">
                                    <Calendar className="size-5" />
                                    View Meal Plans
                                </Link>
                            </Button>
                        </div>
                    )}
                </Card>
            ) : (
                <ul className="space-y-4">
                    {lists.map((list) => {
                        const total = list.items.length;
                        const ticked = list.items.filter((item) => item.isPurchased).length;
                        const percent = total ? Math.round((ticked / total) * 100) : 0;
                        return (
                            <li
                                key={list.id}
                                className="rounded-xl border border-brand-surface-edge bg-brand-surface p-4 shadow-sm sm:p-5"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <Link
                                        to={`/shopping-lists/${list.id}`}
                                        className="group min-w-0 flex-1"
                                    >
                                        <h2 className="text-lg leading-snug font-bold text-foreground group-hover:text-brand-text-blue">
                                            <span className="break-words">{list.name}</span>
                                            <ChevronRight
                                                className="ml-0.5 inline size-5 align-[-3px]"
                                                aria-hidden="true"
                                            />
                                        </h2>
                                        <p className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground">
                                            <Calendar className="size-4" aria-hidden="true" />
                                            {listDates(list)}
                                        </p>
                                    </Link>
                                    <div className="flex shrink-0 items-center gap-2">
                                        {!list.isCompleted && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                disabled={busy}
                                                onClick={() => completeListMutation.mutate(list.id)}
                                                className="text-brand-text-green"
                                            >
                                                <Check className="size-4" />
                                                <span className="hidden sm:inline">Complete</span>
                                            </Button>
                                        )}
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={busy}
                                            aria-label={`Delete ${list.name}`}
                                            onClick={() => handleDeleteList(list.id)}
                                            className="text-red-700"
                                        >
                                            <Trash2 className="size-4" />
                                        </Button>
                                    </div>
                                </div>

                                <Link to={`/shopping-lists/${list.id}`} className="mt-3 block">
                                    <div className="mb-1 flex justify-between text-sm">
                                        <span className="font-semibold text-foreground">
                                            {ticked} of {total} ticked off
                                        </span>
                                        {list.isCompleted && list.completedAt && (
                                            <span className="text-brand-text-green">
                                                Completed{' '}
                                                {new Date(list.completedAt).toLocaleDateString(
                                                    'en-ZA',
                                                    { day: 'numeric', month: 'short' },
                                                )}
                                            </span>
                                        )}
                                    </div>
                                    <div className="h-2 overflow-hidden rounded-full bg-brand-bar-track">
                                        <div
                                            className="h-full rounded-full bg-brand-bar-fill"
                                            style={{ width: `${percent}%` }}
                                        />
                                    </div>
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

const ShoppingLists = () => {
    return (
        <FeatureGate feature={Feature.SHOPPING_LISTS} fallback={<ShoppingListsLockedPage />}>
            <ShoppingListsContent />
        </FeatureGate>
    );
};

export default ShoppingLists;
